import api from "@/app/api";
import type { ProductResponse } from "@/app/api/types";
import type { LocationResponse } from "@/app/api/types/location";
import { PartnerType } from "@/app/api/types/partner";
import type {
  Partner,
  PartnerAccount,
  PartnerAddress,
  PartnerContact,
} from "@/app/api/types/partner";
import type { Partner as V2Partner } from "@stockline/shared/types/crm/partner";
import type { PartnerAddress as V2PartnerAddress } from "@stockline/shared/types/crm/partner-address";
import type { PartnerContact as V2PartnerContact } from "@stockline/shared/types/crm/partner-contact";
import type { ClientGroup as V2ClientGroup } from "@stockline/shared/types/crm/client-group";
import type { ClientGroup as LegacyClientGroup } from "@/app/api/types/client_group";
import { getPartnerStore } from "@/lib/offline-db";
import {
  useAISyncStatus,
  useProducts,
  useUnits,
  usePartners as usePartnersV2,
  usePartnerAddresses as usePartnerAddressesV2,
  usePartnerContacts as usePartnerContactsV2,
  useClientGroups as useClientGroupsV2,
} from "@stockline/web";
import type { Product as V2Product } from "@stockline/shared/types/inventory/product";
import type { Unit as V2Unit } from "@stockline/shared/types/inventory/unit";
import type { ProductUnit } from "@/app/api/types/product";
import type { UnitResponse } from "@/app/api/types/unit";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UUID } from "crypto";

const PARTNER_TYPE_FROM_V2: Record<V2Partner["type"], PartnerType> = {
  CLIENT: PartnerType.CLIENT,
  SUPPLIER: PartnerType.SUPPLIER,
  WHOLESALER: PartnerType.WHOLESALER,
};

function v2GroupRefToLegacy(g: V2ClientGroup): LegacyClientGroup {
  // Legacy `ClientGroup` declares `description`/`ai_context`/`created_at`/
  // `updated_at` as required, but the original REST projection on partner
  // rows never populated them either — keep the same minimal shape via an
  // `unknown` cast so the recap/list/detail surfaces compile unchanged.
  return {
    id: g.id,
    name: g.name,
    color: g.color,
  } as unknown as LegacyClientGroup;
}

/**
 * Map a V2 offline-cache `Partner` (camelCase) onto the legacy snake_case
 * `Partner` shape. The V2 row only carries `clientGroupIds: string[]`
 * pointers; this mapper joins them against an optional `groupsById` lookup
 * (built once at the call site from `useClientGroups()`) to reconstruct the
 * legacy `client_groups: ClientGroup[]` projection. Callers that don't need
 * groups pass no map and pay zero join cost — `client_groups` is `[]` then.
 *
 * `?? []` defends against pre-v29 Dexie records that pre-date the
 * `clientGroupIds` field — `clientGroupIds` is required on the V2 type but
 * older caches still hydrate without it until SSE/full-pull refreshes the row.
 */
function mapV2PartnerToLegacy(
  v2: V2Partner,
  groupsById?: Map<string, LegacyClientGroup>,
): Partner {
  let client_groups: LegacyClientGroup[] = [];
  if (groupsById && groupsById.size > 0) {
    for (const id of v2.clientGroupIds ?? []) {
      const g = groupsById.get(id);
      if (g) client_groups.push(g);
    }
  }
  return {
    id: v2.id as UUID,
    company_id: v2.companyId as UUID,
    name: v2.name,
    usage_name: v2.usageName,
    code: v2.code,
    type: PARTNER_TYPE_FROM_V2[v2.type],
    ai_context: v2.aiContext,
    is_active: v2.isActive,
    is_user_partner: v2.isUserPartner ?? false,
    client_groups,
  };
}

function mapV2AddressToLegacy(v2: V2PartnerAddress): PartnerAddress {
  return {
    id: v2.id as UUID,
    partner_id: v2.partnerId as UUID,
    name: v2.name,
    address_1: v2.address1,
    address_2: v2.address2,
    city: v2.city,
    zip_code: v2.zipCode,
    code: v2.code,
    state: v2.state,
    country: v2.country,
    is_default: v2.isDefault,
    ai_context: v2.aiContext,
  };
}

function mapV2ContactToLegacy(v2: V2PartnerContact): PartnerContact {
  return {
    id: v2.id as UUID,
    partner_id: v2.partnerId as UUID,
    company_owner: v2.companyOwner,
    name: v2.name,
    surname: v2.surname,
    phones: v2.phones,
    emails: v2.emails,
    role: v2.role,
    ai_context: v2.aiContext,
  };
}

/**
 * Staggered cache configs — offset by 5 min so products, partners, and accounts
 * don't all refetch at the same time.
 */
const PRODUCTS_CACHE = {
  staleTime: 25 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
  refetchInterval: 25 * 60 * 1000, // Every 25 min
  refetchIntervalInBackground: true,
};

const PARTNERS_CACHE = {
  staleTime: 30 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
  refetchInterval: 30 * 60 * 1000, // Every 30 min
  refetchIntervalInBackground: true,
};

const ACCOUNTS_CACHE = {
  staleTime: 35 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
  refetchInterval: 35 * 60 * 1000, // Every 35 min
  refetchIntervalInBackground: true,
};

const LOCATIONS_CACHE = {
  staleTime: 40 * 60 * 1000,
  gcTime: 60 * 60 * 1000,
  refetchInterval: 40 * 60 * 1000, // Every 40 min
  refetchIntervalInBackground: true,
};

/**
 * Convert a V2 offline-cache `Unit` (camelCase) into the legacy snake_case
 * `UnitResponse` consumed by the rest of the frontend.
 */
function v2UnitToLegacyResponse(u: V2Unit): UnitResponse {
  return {
    id: u.id as UnitResponse["id"],
    company_id: u.companyId as UnitResponse["company_id"],
    name: u.name,
    abbreviation: u.abbreviation,
    per_box: u.perBox,
    price_per_unit: u.pricePerUnit,
    default: u.default,
    created_at: u.createdAt,
    updated_at: u.updatedAt,
  };
}

/**
 * Map a V2 offline-cache `Product` (camelCase) onto the legacy `ProductResponse`
 * shape (snake_case). `units` is rebuilt from the lightweight `productUnits`
 * refs carried on the product, joined against the offline `units` cache.
 *
 * `is_user_product` now ships on the V2 sync row itself (computed JOIN-side
 * in the V2 list endpoint), so we read it straight off the Dexie record.
 * No more REST-fed Set merge — the legacy `useUserProducts` hook is gone.
 *
 * The legacy unified-products SQL never populated `parents`, `children`,
 * `tags`, or `stock_summary` either, so they remain undefined here.
 */
function v2ProductToLegacyResponse(
  p: V2Product,
  unitsById: Map<string, UnitResponse>,
): ProductResponse {
  const units: ProductUnit[] = [];
  for (const ref of p.productUnits ?? []) {
    const unit = unitsById.get(ref.unitId);
    if (!unit) continue; // unit row not yet synced — skip rather than crash
    units.push({
      unit_id: ref.unitId as ProductUnit["unit_id"],
      is_default: ref.isDefault,
      weight: ref.weight,
      unit,
    });
  }

  return {
    id: p.id as ProductResponse["id"],
    company_id: p.companyId as ProductResponse["company_id"],
    name: p.name,
    short_name: p.shortName,
    sku: p.sku,
    code: p.code,
    piece_weight: p.pieceWeight,
    box_weight: p.boxWeight,
    weight_price: p.weightPrice,
    labels: (p.labels as unknown as ProductResponse["labels"]) ?? null,
    is_sleeping: p.isSleeping,
    ai_context: p.aiContext,
    product_metadata: p.productMetadata as ProductResponse["product_metadata"],
    image_urls: p.imageUrls,
    units,
    is_user_product: p.isUserProduct ?? false,
  };
}

/**
 * Global products cache — backed by the offline-first V2 IndexedDB store.
 *
 * The first read seeds from the V2 API (`GET /api/v2/products`, paginated)
 * when no `sync_id` watermark exists; subsequent updates flow in over SSE
 * (`product.created/updated/deleted`). The store is the single source of
 * truth; this hook re-renders when `notifyStoreUpdate("products")` fires.
 *
 * Behaviour parity with the previous TanStack-backed implementation:
 * - `enabled: false` → returns empty / no-op.
 * - `includeAll: true` → returns the full company catalog.
 * - default → if the user has any UCP-linked products, returns only those;
 *   otherwise returns the full catalog. `is_user_product` is set on every
 *   item so callers that read the flag still see the right value.
 */
export function useGlobalProducts(options?: {
  enabled?: boolean;
  includeAll?: boolean;
}) {
  const enabled = options?.enabled ?? true;
  const includeAll = options?.includeAll ?? false;
  const queryClient = useQueryClient();
  // Both reactive (Dexie liveQuery under the hood — re-renders on every IDB
  // write driven by SSE/sync). They return arrays directly; first paint can
  // be empty while the SyncEngine performs its initial pull.
  const v2Products = useProducts();
  const v2UnitsList = useUnits();
  const { status: syncStatus } = useAISyncStatus();

  const unitsById = useMemo<Map<string, UnitResponse>>(() => {
    const m = new Map<string, UnitResponse>();
    for (const u of v2UnitsList) {
      m.set(u.id, v2UnitToLegacyResponse(u));
    }
    return m;
  }, [v2UnitsList]);

  // Per-product structural sharing cache — same rationale as
  // partnerCacheRef in useGlobalPartners above. Without this, every Dexie
  // tick on products / units re-allocates the 7k-product array with all
  // fresh refs, breaking downstream memos (`productsById`,
  // `enrichRecapPreOrders.productMap`, `useProductImagesBatch`'s queryKey,
  // etc.). Cache key: `${id}|${updatedAt}|unitsRefId` — hits whenever a
  // product's own version is unchanged AND the units cache the legacy
  // mapper reads from is the same ref. When the units cache changes (rare
  // — units rarely touched), we rebuild everything.
  const productCacheRef = useRef(
    new Map<
      string,
      {
        sig: string;
        unitsById: Map<string, UnitResponse>;
        mapped: ProductResponse;
      }
    >(),
  );
  const prevAllProductsRef = useRef<ProductResponse[]>([]);
  const prevProductsUpdatedAtRef = useRef(0);

  // Snapshot `dataUpdatedAt` inside the memo so it only ticks when the
  // underlying V2 array actually changes — keying it on `Date.now()` per
  // render (the previous shape) marked "data updated" on every parent
  // re-render and broke the staleness checks downstream.
  const { allProducts, dataUpdatedAt } = useMemo<{
    allProducts: ProductResponse[];
    dataUpdatedAt: number;
  }>(() => {
    if (!enabled) return { allProducts: [], dataUpdatedAt: 0 };

    const nextCache = new Map<
      string,
      {
        sig: string;
        unitsById: Map<string, UnitResponse>;
        mapped: ProductResponse;
      }
    >();
    const mapped: ProductResponse[] = new Array(v2Products.length);
    for (let i = 0; i < v2Products.length; i++) {
      const p = v2Products[i]!;
      const sig = `${p.id}|${p.updatedAt ?? ""}`;
      const prev = productCacheRef.current.get(p.id);
      let m: ProductResponse;
      if (prev && prev.sig === sig && prev.unitsById === unitsById) {
        m = prev.mapped;
      } else {
        m = v2ProductToLegacyResponse(p, unitsById);
      }
      nextCache.set(p.id, { sig, unitsById, mapped: m });
      mapped[i] = m;
    }
    productCacheRef.current = nextCache;

    // Array-level structural sharing: if every item ref matches the prev
    // result, reuse the prev array so downstream `[products]` useMemo
    // deps don't fire.
    const prev = prevAllProductsRef.current;
    if (prev.length === mapped.length) {
      let allSame = true;
      for (let i = 0; i < mapped.length; i++) {
        if (prev[i] !== mapped[i]) {
          allSame = false;
          break;
        }
      }
      if (allSame) {
        return {
          allProducts: prev,
          dataUpdatedAt: prevProductsUpdatedAtRef.current,
        };
      }
    }
    prevAllProductsRef.current = mapped;
    const now = Date.now();
    prevProductsUpdatedAtRef.current = now;
    return { allProducts: mapped, dataUpdatedAt: now };
  }, [enabled, v2Products, unitsById]);

  // Same filtering rule as the legacy hook: prefer user-linked products when
  // any exist, otherwise fall back to the full company catalog.
  const data = useMemo<ProductResponse[]>(() => {
    if (!enabled) return [];
    if (includeAll) return allProducts;
    const userOnly = allProducts.filter((p) => p.is_user_product);
    return userOnly.length > 0 ? userOnly : allProducts;
  }, [enabled, includeAll, allProducts]);

  // Mirror into the TanStack cache so legacy hooks (`useProduct`,
  // `useProductBySku`, `useProductByCode`) that still call
  // `queryClient.getQueryData(["products", "global"])` keep working.
  useEffect(() => {
    if (!enabled) return;
    queryClient.setQueryData(["products", "global"], allProducts);
  }, [enabled, allProducts, queryClient]);

  // Loading is "still on first sync with no data yet" — once anything lands
  // in either store we stop spinning. Engine status drops to "idle"/"online"
  // when the initial pull completes even if the company has zero rows.
  const isLoading =
    enabled &&
    syncStatus === "syncing" &&
    v2Products.length === 0 &&
    v2UnitsList.length === 0;
  const isError = syncStatus === "error";

  // TanStack-compatible status fields. An errored SyncEngine surfaces as
  // status: "error" + isSuccess: false so consumers gating on isSuccess
  // don't render off a stale empty cache.
  const status = isError
    ? ("error" as const)
    : !enabled || !isLoading
      ? ("success" as const)
      : ("pending" as const);
  const fetchStatus =
    !enabled || !isLoading ? ("idle" as const) : ("fetching" as const);

  return {
    data,
    allProducts,
    isLoading,
    isFetching: isLoading,
    isError,
    error: null as Error | null,
    isPending: isLoading,
    isSuccess: enabled && !isError ? !isLoading : false,
    status,
    fetchStatus,
    // Snapshotted inside the data memo so the value only ticks on real
    // changes — staleness comparisons elsewhere depend on this.
    dataUpdatedAt,
    // Refetch is a no-op: the SyncEngine in @stockline/web auto-pulls on
    // mount, on reconnect, and on SSE events. Kept for API compatibility.
    refetch: async () => {},
  };
}

/**
 * Global partners cache — fully offline-first reads.
 *
 * The list is sourced from the V2 sync engine's reactive Dexie store
 * (`usePartners()` from `@stockline/web`); the V2 partner row carries
 * `clientGroupIds: string[]` directly, so the legacy REST `load_all`
 * enrichment fan-out is gone. Consumers that need to render
 * group badges/colors join `clientGroupIds` against `useClientGroups()` at
 * the call site — the mapper above leaves `client_groups: []` on every row
 * to avoid forcing every consumer to pay the join cost.
 *
 * Addresses and contacts are likewise V2-owned via `usePartnerAddresses()` /
 * `usePartnerContacts()` and spliced onto each Partner row below.
 *
 * The legacy `partnerStore` (temp-`partner-*` IDs from offline create flow)
 * is still merged in — write-side migration is intentionally out of scope.
 */
export function useGlobalPartners(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();

  // V2 reactive Dexie — primary list source, works offline.
  const v2Partners = usePartnersV2();
  // Pull all addresses and contacts unfiltered: useGlobalPartners is a list
  // surface (picker, search, popovers), so consumers need every partner's
  // relations available without a per-id round-trip. Reactive Dexie
  // re-renders this hook on every SSE-driven write.
  const v2Addresses = usePartnerAddressesV2();
  const v2Contacts = usePartnerContactsV2();
  // Client groups join with `partner.clientGroupIds` to reconstruct the
  // legacy `partner.client_groups` projection — kept inside the list hook
  // so recap/partners surfaces that filter/display by group keep working
  // without each call site having to wire the join itself.
  const v2Groups = useClientGroupsV2();
  const { status: syncStatus } = useAISyncStatus();

  // Legacy offline-only partners (temp-partner-* IDs not yet synced).
  const [offlinePartners, setOfflinePartners] = useState<Partner[]>([]);

  const loadOfflinePartners = useCallback(async () => {
    try {
      const partnerStore = getPartnerStore();
      const cached = await partnerStore.getOfflinePartners();
      setOfflinePartners(cached.map((c) => c.partner));
    } catch (error) {
      console.error(
        "[useGlobalPartners] Failed to load offline partners:",
        error,
      );
    }
  }, []);

  useEffect(() => {
    loadOfflinePartners();
  }, [loadOfflinePartners]);

  useEffect(() => {
    const handleOnline = () => {
      setTimeout(loadOfflinePartners, 2000);
    };
    window.addEventListener("online", handleOnline);
    return () => window.removeEventListener("online", handleOnline);
  }, [loadOfflinePartners]);

  // Group V2 addresses/contacts by partnerId so we can splice them onto
  // every Partner row in one pass below. Reactive Dexie re-renders this
  // hook on every SSE-driven write.
  const v2AddressesByPartnerId = useMemo(() => {
    const m = new Map<string, PartnerAddress[]>();
    for (const addr of v2Addresses ?? []) {
      let list = m.get(addr.partnerId);
      if (!list) {
        list = [];
        m.set(addr.partnerId, list);
      }
      if (!addr.deletedAt) list.push(mapV2AddressToLegacy(addr));
    }
    return m;
  }, [v2Addresses]);

  const v2ContactsByPartnerId = useMemo(() => {
    const m = new Map<string, PartnerContact[]>();
    for (const c of v2Contacts ?? []) {
      let list = m.get(c.partnerId);
      if (!list) {
        list = [];
        m.set(c.partnerId, list);
      }
      if (!c.deletedAt) list.push(mapV2ContactToLegacy(c));
    }
    return m;
  }, [v2Contacts]);

  const groupsById = useMemo(() => {
    const m = new Map<string, LegacyClientGroup>();
    for (const g of v2Groups ?? []) {
      if (g.deletedAt) continue;
      m.set(g.id, v2GroupRefToLegacy(g));
    }
    return m;
  }, [v2Groups]);

  // Per-partner structural sharing cache. Without this, every Dexie tick
  // on partners / addresses / contacts / groups re-allocates the full
  // 14k-partner array with all-fresh `Partner` refs, which cascades into
  // every downstream consumer (`partnersById`, `completePriceMaps`,
  // `enrichRecapPreOrders`, recap context, …).
  //
  // A cache entry pins, by reference, every input its `mapped` Partner was
  // built from: the `sig` (partner version + which relation sub-arrays were
  // spliced) AND the `groupsRef` (`groupsById` map identity) used for the
  // `client_groups` join. A hit requires all of them to match the current
  // render's inputs; any mismatch rebuilds that one partner.
  //
  // `groupsRef` is what makes the warm-start race safe: the 14k-row partners
  // `useLiveQuery` can resolve BEFORE the small client-groups one, so partners
  // first map against an empty `groupsById` (`client_groups: []`). When groups
  // land, `groupsById` changes identity, every entry's `groupsRef` mismatches,
  // and the projection rebuilds — instead of serving the stale-empty groups
  // that would blank recap pricing (`useRecapData` derives `partnerGroups`
  // from `client_groups`). Comparing the per-entry ref — rather than a separate
  // mutable ref written inside the memo — keeps the check idempotent under
  // React StrictMode's double-invocation of `useMemo`.
  //
  // Known limitation, intentional for perf: an address/contact CONTENT change
  // that doesn't move the sub-array reference wouldn't surface on the cached
  // partner — but `v2AddressesByPartnerId` always rebuilds the per-partner
  // array on any v2Addresses tick, so the ref changes whenever addresses are
  // touched, which busts the entry correctly.
  const partnerCacheRef = useRef(
    new Map<
      string,
      {
        sig: string;
        mapped: Partner;
        groupsRef: Map<string, LegacyClientGroup>;
      }
    >(),
  );
  const prevMergedRef = useRef<Partner[]>([]);
  const prevDataUpdatedAtRef = useRef(0);

  // Build the canonical list directly off V2. The V2 partner row now carries
  // `clientGroupIds`, so no REST overlay is required.
  // Capture `dataUpdatedAt` inside the memo so it only ticks when `mergedData`
  // actually changes — keying it on `Date.now()` per render (the previous
  // shape) would mark "data updated" on every parent re-render, breaking the
  // staleness checks downstream consume.
  const { mergedData, dataUpdatedAt } = useMemo<{
    mergedData: Partner[];
    dataUpdatedAt: number;
  }>(() => {
    if (!enabled) return { mergedData: [], dataUpdatedAt: 0 };

    const withV2Relations = (
      p: Partner,
      v2Addrs: PartnerAddress[] | undefined,
      v2Cts: PartnerContact[] | undefined,
    ): Partner => {
      if (!v2Addrs && !v2Cts) return p;
      return {
        ...p,
        addresses: v2Addrs ?? p.addresses,
        contacts: v2Cts ?? p.contacts,
      };
    };

    const nextCache = new Map<
      string,
      {
        sig: string;
        mapped: Partner;
        groupsRef: Map<string, LegacyClientGroup>;
      }
    >();
    const visible: Partner[] = [];
    for (const p of v2Partners ?? []) {
      if (p.deletedAt) continue;
      const v2Addrs = v2AddressesByPartnerId.get(p.id as string);
      const v2Cts = v2ContactsByPartnerId.get(p.id as string);
      // Pid-scoped ref identifiers piggyback off the existing reference
      // equality of the per-partner sub-arrays — no per-row content hash
      // needed. When v2Addresses ticks, the per-partner array ref is
      // rebuilt, so this sig invalidates correctly for affected partners
      // only (the unrelated ones reuse cached refs).
      const sig = `${p.updatedAt ?? ""}|${v2Addrs ? "A" : "a"}|${v2Cts ? "C" : "c"}`;
      const prev = partnerCacheRef.current.get(p.id as string);
      let mapped: Partner;
      // Cache hit only when the same partner produces the same mapped ref +
      // the relation arrays AND the groups lookup at the SAME refs we built it
      // from. Reference-equality on v2Addrs/v2Cts/groupsById is the right gate:
      // if those are the same refs from last render the mapped partner is still
      // valid (withV2Relations spreads relations through, and the
      // `client_groups` projection was joined against that same `groupsById`).
      if (
        prev &&
        prev.groupsRef === groupsById &&
        prev.sig === sig &&
        prev.mapped.addresses === v2Addrs &&
        prev.mapped.contacts === v2Cts
      ) {
        mapped = prev.mapped;
      } else {
        mapped = withV2Relations(
          mapV2PartnerToLegacy(p, groupsById),
          v2Addrs,
          v2Cts,
        );
      }
      nextCache.set(p.id as string, { sig, mapped, groupsRef: groupsById });
      visible.push(mapped);
    }
    partnerCacheRef.current = nextCache;

    let result: Partner[];
    if (offlinePartners.length === 0) {
      result = visible;
    } else {
      const knownNames = new Set(visible.map((p) => p.name.toLowerCase()));
      const uniqueLegacyOffline = offlinePartners.filter(
        (p) => !knownNames.has(p.name.toLowerCase()),
      );
      result = [...uniqueLegacyOffline, ...visible];
    }

    // Array-level structural sharing: if every item ref matches the prev
    // result and the length is the same, reuse the prev array so
    // downstream `[partners]` useMemo deps don't fire. This is the lever
    // that prevents the partnersById / completePriceMaps cascade in
    // useRecapData on every Dexie tick.
    const prev = prevMergedRef.current;
    if (prev.length === result.length) {
      let allSame = true;
      for (let i = 0; i < result.length; i++) {
        if (prev[i] !== result[i]) {
          allSame = false;
          break;
        }
      }
      if (allSame) {
        // No partner reference changed — keep the prev array AND prev
        // dataUpdatedAt. Downstream stays stable.
        return {
          mergedData: prev,
          dataUpdatedAt: prevDataUpdatedAtRef.current,
        };
      }
    }
    prevMergedRef.current = result;
    const now = Date.now();
    prevDataUpdatedAtRef.current = now;
    return { mergedData: result, dataUpdatedAt: now };
  }, [
    enabled,
    v2Partners,
    offlinePartners,
    v2AddressesByPartnerId,
    v2ContactsByPartnerId,
    groupsById,
  ]);

  // Mirror the merged list into the TanStack cache so legacy hooks
  // (`usePartner`, code paths reading `["partners","global"]` directly) keep
  // resolving without a network round-trip.
  useEffect(() => {
    if (!enabled) return;
    queryClient.setQueryData(["partners", "global"], mergedData);
  }, [enabled, mergedData, queryClient]);

  // Loading is "still on first sync with no data yet" — once anything lands
  // in the V2 store we stop spinning. SyncEngine status drops to "idle" /
  // "online" when the initial pull completes even if the company has zero
  // rows.
  const isLoading =
    enabled && syncStatus === "syncing" && (v2Partners?.length ?? 0) === 0;
  const isError = syncStatus === "error";

  // Map onto the TanStack status taxonomy. An errored SyncEngine surfaces as
  // status: "error" + isSuccess: false so consumers that gate on isSuccess
  // (recap shells, partner pickers) don't render off a stale empty cache.
  const status = isError
    ? ("error" as const)
    : !enabled || !isLoading
      ? ("success" as const)
      : ("pending" as const);
  const fetchStatus =
    !enabled || !isLoading ? ("idle" as const) : ("fetching" as const);

  return {
    data: mergedData,
    offlinePartners,
    refreshOfflinePartners: loadOfflinePartners,
    isLoading,
    isFetching: isLoading,
    isError,
    error: null as Error | null,
    isPending: isLoading,
    isSuccess: enabled && !isError ? !isLoading : false,
    status,
    fetchStatus,
    // Snapshotted inside the data memo so the value only ticks on real
    // changes — TanStack staleness comparisons elsewhere depend on this.
    dataUpdatedAt,
    // Refetch is a no-op: the SyncEngine in @stockline/web auto-pulls on
    // mount, on reconnect, and on SSE events. Kept for API compatibility.
    refetch: async () => {},
  };
}

/**
 * Global partner accounts cache - loads ALL partner accounts for the company
 * Uses the bulk endpoint to fetch all accounts in a single request
 */
export function useGlobalPartnerAccounts(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["partner-accounts", "global"],
    queryFn: async () => {
      // Fetch all partner accounts using the bulk endpoint
      const accounts = await api.partners.getAllAccounts({
        load_all: true, // Get all accounts without pagination
      });

      // Convert array to Map for fast lookup by partner_id
      const accountsMap = new Map<string, PartnerAccount>();
      accounts.forEach((account) => {
        // Convert PartnerAccountSummary to full PartnerAccount format
        // Note: Some fields will be missing but the essential financial data is there
        const fullAccount: PartnerAccount = {
          id: account.id,
          partner_id: account.partner_id,
          company_id: undefined as any, // Not returned in summary
          total_debt: account.total_debt,
          past_due_amount: account.past_due_amount,
          not_due_yet_amount: account.not_due_yet_amount,
          credit_limit: account.credit_limit,
          total_exposure: account.total_exposure,
          is_blocked: account.is_blocked,
          days_past_due: account.days_past_due,
          notes: null, // Not returned in summary
          last_updated_at: new Date().toISOString(), // Not returned in summary
          created_at: new Date().toISOString(), // Not returned in summary
          updated_at: new Date().toISOString(), // Not returned in summary
          credit_available:
            account.credit_limit != null
              ? account.credit_limit -
                (account.total_exposure ?? account.total_debt)
              : null,
          is_over_limit: account.is_over_limit,
          has_past_due: account.has_past_due,
        };
        accountsMap.set(account.partner_id, fullAccount);
      });

      return accountsMap;
    },
    ...ACCOUNTS_CACHE,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Global locations cache - loads ALL active locations for the company
 */
export function useGlobalLocations(options?: { enabled?: boolean }) {
  return useQuery<LocationResponse[]>({
    queryKey: ["locations", "global"],
    queryFn: async () => {
      return api.locations.list({ is_active: true });
    },
    ...LOCATIONS_CACHE,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Global routes cache - loads all active delivery routes for the company.
 * Used by the recap RouteSelector when the `pickDelivery` feature flag is on.
 * Mirrors the product/partner/location global-cache pattern so the recap
 * never triggers per-pre-order fetches.
 */
export function useGlobalRoutes(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["routes", "global"],
    queryFn: () => api.routes.list({ is_active: true }),
    ...LOCATIONS_CACHE,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Manual refresh function for locations
 */
export function useManualRefreshLocations() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: ["locations", "global"],
    });
  }, [queryClient]);
}

/**
 * Manual refresh function for products
 */
export function useManualRefreshProducts() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: ["products", "global"],
    });
  }, [queryClient]);
}

/**
 * Manual refresh function for partners
 */
export function useManualRefreshPartners() {
  const queryClient = useQueryClient();

  return useCallback(() => {
    return queryClient.invalidateQueries({
      queryKey: ["partners", "global"],
    });
  }, [queryClient]);
}

/**
 * Prefetch products and locations for app initialization. Partners are NOT
 * prefetched here anymore — the V2 SyncEngine performs the partner cold load
 * automatically once `AISyncProvider` mounts, and `useGlobalPartners` reads
 * directly from the reactive Dexie store.
 */
export async function prefetchUnifiedCache(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  await Promise.all([
    queryClient.prefetchQuery({
      queryKey: ["products", "global"],
      queryFn: async () => {
        const response = await api.products.getProducts({
          include_sleeping: true,
          user_products_only: false,
          load_all: true,
        });
        return response.items || [];
      },
      staleTime: PRODUCTS_CACHE.staleTime,
    }),
    queryClient.prefetchQuery({
      queryKey: ["locations", "global"],
      queryFn: async () => {
        return api.locations.list({ is_active: true });
      },
      staleTime: LOCATIONS_CACHE.staleTime,
    }),
  ]);
}

/**
 * Get cache metrics for monitoring
 */
export function useCacheMetrics() {
  const queryClient = useQueryClient();
  const productsQuery = useGlobalProducts();
  const partnersQuery = useGlobalPartners();

  return useMemo(() => {
    const productsState = queryClient.getQueryState(["products", "global"]);
    const partnersState = queryClient.getQueryState(["partners", "global"]);

    return {
      productsCount: productsQuery.data?.length || 0,
      partnersCount: partnersQuery.data?.length || 0,
      isProductsLoading: productsQuery.isLoading,
      isPartnersLoading: partnersQuery.isLoading,
      lastProductsPolled: productsState?.dataUpdatedAt || null,
      lastPartnersPolled: partnersState?.dataUpdatedAt || null,
      productsError: productsQuery.error,
      partnersError: partnersQuery.error,
    };
  }, [
    queryClient,
    productsQuery.data,
    partnersQuery.data,
    productsQuery.isLoading,
    partnersQuery.isLoading,
    productsQuery.error,
    partnersQuery.error,
  ]);
}
